import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiGet } from './client';

export type StatsRange = 'today' | '7d' | '30d';

export interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'unreachable';
  queueDepth?: number;
}

export interface DashboardHealth {
  services: ServiceHealth[];
  partial: boolean;
  generatedAt: string;
}

export interface VolumeData {
  range: StatsRange;
  scope: 'platform' | 'org';
  orgId: string | null;
  cases: { today: number; thisWeek: number; thisMonth: number; inRange: number };
  perOrg: Array<{ orgId: string; name: string; count: number }>;
  perProduct: Array<{ productCode: string; count: number }>;
  perOrgProduct: Array<{
    orgId: string;
    orgName: string;
    productCode: string;
    count: number;
  }>;
  /**
   * Cases created per day over the selected range, oldest first,
   * zero-filled. `byOrg` keys into `casesPerDayOrgs` for the stacked
   * bar chart.
   */
  casesPerDay: Array<{ date: string; total: number; byOrg: Record<string, number> }>;
  /** Orgs that appear in any `casesPerDay` bucket (for chart series). */
  casesPerDayOrgs: Array<{ orgId: string; name: string }>;
}

export interface VolumeResponse {
  data: VolumeData | null;
  partial: boolean;
  degradedFor: string[];
  generatedAt: string;
}

export interface AiReviewData {
  inferences: {
    today: number;
    /** All-time count of AI inference results (cases with a diagnosis). */
    totalInferences: number;
    successInRange: number;
    failureInRange: number;
    successRateInRange: number;
    avgLatencyMsInRange: number | null;
  };
  queueDepth: number;
  recentFailures: Array<{ at: string; reason: string }>;
  /** All-time counts of `analysisResult` values. Drives the dashboard donut chart. */
  analysisResultBreakdown: Array<{ result: string; count: number }>;
}

export interface AiReviewResponse {
  data: AiReviewData | null;
  partial: boolean;
  degradedFor: string[];
  generatedAt: string;
}

export interface HumanReviewData {
  queue: { open: number; claimed: number; submitted24h: number };
  avgTimeToDecisionMs24h: number | null;
  declineCount24h: number;
}

export interface HumanReviewResponse {
  data: HumanReviewData | null;
  partial: boolean;
  degradedFor: string[];
  generatedAt: string;
}

export interface OrgDetailResponse {
  orgId: string;
  volume: VolumeData | null;
  aiReview: AiReviewData | null;
  humanReview: HumanReviewData | null;
  partial: boolean;
  degradedFor: string[];
  generatedAt: string;
}

function buildUrl(path: string, range: StatsRange, org: string | null): string {
  const params = new URLSearchParams({ range });
  if (org) params.set('org', org);
  return `${path}?${params.toString()}`;
}

/**
 * Dashboard tiles poll on a 30-second cadence so operators get fresh
 * numbers without manually refreshing. Polling pauses when the tab is
 * hidden (refetchIntervalInBackground=false) to avoid burning API
 * quota on inactive windows. React Query already refetches on window
 * focus, so coming back to a stale tab triggers an immediate update.
 */
const AUTO_REFRESH_MS = 30_000;

export function useHealth() {
  return useQuery({
    queryKey: ['dashboard', 'health'],
    queryFn: () => apiGet<DashboardHealth>('/api/dashboard/health'),
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

// `placeholderData: keepPreviousData` keeps the previous range's data
// on screen while a new range fetches, so `isLoading` only flips true
// on the very first load — not on every dropdown change. That keeps
// MetricCard, AnimatedNumber, and the charts mounted across range
// switches so the count-up / bar / donut animations actually tween
// from the old values to the new ones instead of snapping in after a
// loader flicker.
export function useVolume(range: StatsRange = '7d', org: string | null = null) {
  return useQuery({
    queryKey: ['dashboard', 'volume', range, org],
    queryFn: () => apiGet<VolumeResponse>(buildUrl('/api/dashboard/volume', range, org)),
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });
}

export function useAiReview(range: StatsRange = '7d', org: string | null = null) {
  return useQuery({
    queryKey: ['dashboard', 'ai-review', range, org],
    queryFn: () => apiGet<AiReviewResponse>(buildUrl('/api/dashboard/ai-review', range, org)),
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });
}

export function useHumanReview(range: StatsRange = '7d', org: string | null = null) {
  return useQuery({
    queryKey: ['dashboard', 'human-review', range, org],
    queryFn: () => apiGet<HumanReviewResponse>(buildUrl('/api/dashboard/human-review', range, org)),
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });
}

export { AUTO_REFRESH_MS };

export function useOrgDetail(orgId: string, range: StatsRange = '7d') {
  return useQuery({
    queryKey: ['dashboard', 'org-detail', orgId, range],
    queryFn: () => apiGet<OrgDetailResponse>(`/api/dashboard/orgs/${orgId}?range=${range}`),
    enabled: orgId.length > 0,
  });
}
